/* eslint-disable no-use-before-define */
// import clevertap from "clevertap-web-sdk";
import isMobile, {
  getBrowserName,
  getOperatingSystem,
  parseEdgeHeaderLocation,
  removeEmptyNullAndUndefinedFromObject,
} from "../utils/common";
import { campaignEvents, cleverTapCommonEvents, cleverTapCommonWords, eventsWithIpAddress } from "./clevertapConstants";
import CookieUtil from "../utils/storage/cookieUtil";
import { fetchCTDisabledEventList, fetchGeoLocation } from "../api/common";
import { getCleverTapPageType, getPageNameForCleverTap, getPageTypeFromURL } from "../utils/fnpPageType";
import { getEntryAccuracy } from "../utils/locationLock";
import { execDataLayer } from "../utils/cache-layer/cacheLayer";
import { getLocalStorageItem, setLocalStorageItem } from "../utils/storage/localStorageUtil";
import { endpointConst } from "../utils/cache-layer/cacheLayerConstants";
import { constants } from "../constants/pageConstants/pdpConstants";
import { HDFC_SMARTBUY, PARTNER } from "../constants/storage";
import { usePathname } from "next/navigation";
import { useSelector } from "react-redux";

let clevertap;

/**
 *
 * @returns
 */
const useCleverTap = () => {
  const cookieTTL = 20 * 60; // 20 minutes
  const options = {
    maxAge: cookieTTL,
    path: "/",
  };
  const LAST_ACTIVITY_KEY = "cleverTapLastActivityExpiresAt";
  const pathname = usePathname();
  const edgeHeader = useSelector((state) => state?.currencyList?.edgeHeader);
  const { countryCode: browsingCountry, city: browsingCity } = parseEdgeHeaderLocation(edgeHeader);

  /**
   * Adds Akamai-derived browsing location to CleverTap event payload.
   * @param {Object} eventData - CleverTap event payload
   * @param {Object} [geoJSON] - Optional geo fallback when edge header is unavailable
   * @returns {Object} Updated event payload
   */
  const applyBrowsingLocation = (eventData, geoJSON) => {
    eventData.browsing_city = browsingCity || geoJSON?.city || "";
    eventData.browsing_country = browsingCountry || geoJSON?.country || "";
    return eventData;
  };

  /**
   *
   */
  const init = () => {
    try {
      if (typeof window !== "undefined") {
        clevertap = require("clevertap-web-sdk"); // eslint-disable-line global-require
        clevertap.init(process.env.NEXT_PUBLIC_CLEVER_TAP_ACCOUNT_ID, process.env.NEXT_PUBLIC_CLEVER_TAP_REGION);
        clevertap.privacy.push({ useIP: true });
      }
      setTimeout(() => {
        const cleverTapID = clevertap?.getCleverTapID();
        const checkLastInActive = checkLastActivityTime(Date.now());
        if (cleverTapID) {
          customWebSession(
            {
              page_name: getPageNameForCleverTap(window?.fnpPageType),
              page_type: getCleverTapPageType(window?.fnpPageType),
            },
            checkLastInActive,
            cleverTapID,
          );
        }
      }, 500);
      // clevertap.setDebugLevel(3); // Debug level can be adjusted based on needs
      console.log("CleverTap Initialized");
    } catch (error) {
      console.log("Clevertap not initialized properly");
    }
  };

  /**
   * To get the unique session id.
   * @returns
   */
  const getSessionID = () => {
    const timeStamp = Date.now();
    const generatedSessionID = `${isMobile() ? "m" : "d"}_${timeStamp}_${Math.floor(Math.random() * 100)}`;
    return generatedSessionID;
  };

  /**
   * To track user activity
   */
  const updateLastActivityTime = (forceUpdate) => {
    const isInactiveStatus = checkLastActivityTime(Date.now());
    if (!isInactiveStatus || forceUpdate) {
      const newExpiry = Date.now() + cookieTTL * 1000;
      setLocalStorageItem(LAST_ACTIVITY_KEY, newExpiry.toString());
      const cleverTapUserSessionID = CookieUtil.getCookie("cleverTapUserSessionID");
      const fetchSessionID = cleverTapUserSessionID || getSessionID();
      CookieUtil.setCookie("cleverTapUserSessionID", fetchSessionID, options);
      CookieUtil.setCookie("cleverTapWebSession", "webSessionStarted", options);
    }
  };

  /**
   * To check the last activity time of user.
   * If the user is inactive for 20 minutes, it will trigger a custom web session event.
   */
  const checkLastActivityTime = (now) => {
    const lastActivityExpiresAt = parseInt(getLocalStorageItem(LAST_ACTIVITY_KEY), 10);
    if (!lastActivityExpiresAt) {
      return true;
    }
    const isInactive = now > lastActivityExpiresAt;
    return isInactive;
  };

  /**
   * It will add analytics_session_id in ctap data.
   * @param {*} cTapData
   * @param {*} checkLastInActive
   * @returns
   */
  const updateSessionIDEventProperties = (cTapData, checkLastInActive) => {
    const cleverTapUserSessionID = CookieUtil.getCookie("cleverTapUserSessionID");
    const fetchSessionID = cleverTapUserSessionID || getSessionID();
    if (!cleverTapUserSessionID && checkLastInActive) {
      CookieUtil.setCookie("cleverTapUserSessionID", fetchSessionID, options);
    }
    cTapData.analytics_sessionid = fetchSessionID;
    return cTapData;
  };
  /**
   * Pushes a CleverTap event if it is not present in the disabled events list.
   * Fetches the list of disabled events, checks if the given eventName is allowed,
   * and pushes the event to CleverTap if enabled.
   *
   * @async
   * @param {string} eventName - The name of the CleverTap event to push.
   * @param {Object} eventData - The data payload to send with the event.
   * @returns {Promise<void|*>} Returns the result of clevertap.event.push if the event is enabled, otherwise undefined.
   */
  const pushCleverTapEventIfEnabled = async (eventName, eventData) => {
    const cleanedEventData = removeEmptyNullAndUndefinedFromObject(eventData);
    if (cleanedEventData?.page_type) {
      cleanedEventData.page_type = getCleverTapPageType(cleanedEventData.page_type);
    }

    let disabledEvents = await execDataLayer(fetchCTDisabledEventList, endpointConst.ctDisabledEvents);
    if (disabledEvents?.status) {
      return clevertap.event.push(eventName, cleanedEventData);
    } else {
      if (!disabledEvents.includes(eventName)) {
        return clevertap.event.push(eventName, cleanedEventData);
      }
    }
  };

  /**
   *
   * @param {*} eventName
   * @param {*} properties
   */
  const trackEventCleverTap = async (eventName, properties = {}) => {
    try {
      if (Object.values(campaignEvents).includes(eventName)) {
        clevertap?.[eventName](properties);
      } else {
        const checkLastInActive = checkLastActivityTime(Date.now());
        await customWebSession(properties, checkLastInActive);
        const cleverTapEventData = updateSessionIDEventProperties(properties, checkLastInActive);
        const geoJSON = await execDataLayer(fetchGeoLocation, "geojs");
        applyBrowsingLocation(cleverTapEventData, geoJSON);
        if (eventsWithIpAddress.includes(eventName)) {
          cleverTapEventData.ip_address = geoJSON?.ip;
        }
        !checkLastInActive && updateLastActivityTime(true);
        await pushCleverTapEventIfEnabled(eventName, cleverTapEventData);
      }
    } catch (error) {
      console.log("Clevertap not initialized properly");
    }
  };

  /**
   * Helper function to wait for CleverTap ID to become available
   * @param {number} maxAttempts - Maximum number of attempts to get the ID
   * @param {number} interval - Time to wait between attempts in milliseconds
   * @returns {Promise<string|null>} CleverTap ID if found, null otherwise
   */
  const waitForCleverTapId = async (maxAttempts = 3, interval = 10) => {
    for (let i = 0; i < maxAttempts; i++) {
      const id = window?.clevertap?.getCleverTapID();
      if (id) return id;
      // eslint-disable-next-line no-await-in-loop
      await new window.Promise((resolve) => setTimeout(resolve, interval));
    }
    return null;
  };

  /**
   * It will get triggered only one time of user inactivity of 20 minutes.
   */
  const customWebSession = async (properties, checkLastInActive, cleverTapID = "") => {
    const cleverTapWebSession = CookieUtil.getCookie("cleverTapWebSession");
    const isSmartBuyUser = CookieUtil.getCookie(PARTNER) === HDFC_SMARTBUY;
    const searchQuery = Object.fromEntries(new URLSearchParams(window.location.search));
    const { utm_source: utmSource, utm_medium: utmMedium, utm_campaign: utmCampaign, gclid } = searchQuery;

    if (!cleverTapWebSession && checkLastInActive) {
      const cTapId = cleverTapID || properties?.ctid || (await waitForCleverTapId());
      const referrer = document?.referrer || "";
      const gateLocationAccuracy = getEntryAccuracy();
      const payload = {
        browser: getBrowserName(),
        page_name: properties?.page_name || getPageTypeFromURL(pathname),
        page_type: getCleverTapPageType(properties?.page_type || getPageTypeFromURL(pathname)),
        complete_url: window.location.href,
        clean_url: `${window.location.origin}${window.location.pathname}`,
        operating_system: getOperatingSystem(),
        utm_source_name: utmSource,
        utm_medium_name: utmMedium,
        utm_campaign_name: utmCampaign,
        login_status: properties?.login_status,
        ctid: cTapId,
        platform: properties?.platform || (isMobile() ? "mobile" : "desktop"),
        webVersion: "v2",
        referrer,
        gclid,
        is_smartbuy_user: isSmartBuyUser,
        ...(gateLocationAccuracy ? { gate_location_accuracy: gateLocationAccuracy } : {}),
      };
      if (getCleverTapPageType(properties?.page_type) === cleverTapCommonWords.PDP) {
        payload.product_id = CookieUtil.getCookie(constants?.CURRENT_PRODUCT_ID);
      }

      CookieUtil.setCookie("cleverTapWebSession", "webSessionStarted", options);
      const geoJSON = await execDataLayer(fetchGeoLocation, "geojs");
      const cleverTapEventData = updateSessionIDEventProperties(payload, checkLastInActive);
      cleverTapEventData.ip_address = geoJSON?.ip;
      applyBrowsingLocation(cleverTapEventData, geoJSON);
      await pushCleverTapEventIfEnabled(cleverTapCommonEvents.sessionStart, cleverTapEventData);

      updateLastActivityTime(true);
    }
  };

  /**
   *
   * @param {*} properties
   */
  const onUserLogin = (properties) => {
    try {
      clevertap.onUserLogin.push(properties);
    } catch (error) {
      console.log("Clevertap not initialized properly");
    }
  };

  return { init, trackEventCleverTap, onUserLogin };
};

export default useCleverTap;

/**
 * showCleverTapNotification this function handle notification for web-native campaign
 * Notification Popup will not get triggered in case of (iOS or MacOS) with Safari Browser.
 */
export const showCleverTapNotification = () => {
  try {
    if (
      typeof window !== "undefined" &&
      typeof clevertap !== "undefined" &&
      !((getOperatingSystem() === "iOS" || getOperatingSystem() === "MacOS") && getBrowserName() === "Safari") &&
      typeof clevertap.notifications !== "undefined" &&
      typeof clevertap.notifications.push === "function"
    ) {
      clevertap.notifications.push({
        titleText: "Stay Updated!",
        bodyText: "Allow notifications to receive the latest offers and updates.",
        okButtonText: "Allow",
        rejectButtonText: "No, Thanks",
        askAgainTimeInSeconds: 86400,
        okButtonColor: "#737530",
        serviceWorkerPath: "/clevertap_sw.js",
      });
    } else {
      console.warn("⚠️ CleverTap is not available or not initialized yet.");
    }
  } catch (error) {
    console.error("❌ Failed to show CleverTap notification:", error);
  }
};
